import { ethers } from "ethers";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DataContext } from "../../context/DataContext";
import { ReactComponent as Copy } from "../../assets/svg/copy.svg";
import {
  IRewardVolumeAllocationRule,
  useRewardVolumeAllocations,
} from "../../hooks/api/admin/useRewardVolumeAllocations";
import { ToastEnum, ToastStatusEnum, useToast } from "../../hooks/toast";
import {
  AccountTag,
  AccountWithTag,
  AddressInput,
  AllocationHeader,
  AllocationRow,
  AllocationSummary,
  CardHeader,
  CardSubtitle,
  CardTitle,
  CompactButton,
  EmptyState,
  ErrorText,
  IconButton,
  Input,
  PanelToolbar,
  PanelToolbarActions,
  PercentageInputWrap,
  PercentageSuffix,
  RuleActions,
  RuleFooter,
  RuleHeader,
  RuleSection,
  RulesList,
  SourceAccount,
  SourceAddress,
  SourceLabel,
  ToolPanel,
} from "./style";

interface IAllocationForm {
  account: string;
  percentage: string;
}

interface IRuleForm {
  key: string;
  persistedSource: string | null;
  sourceAccount: string;
  allocations: IAllocationForm[];
}

interface IRuleValidation {
  sourceValid: boolean;
  duplicateSource: boolean;
  targetErrors: boolean[];
  percentageErrors: boolean[];
  duplicateTargets: boolean;
  totalBasisPoints: number;
  valid: boolean;
}

interface IRewardVolumeAllocationsProps {
  previewRules?: IRewardVolumeAllocationRule[];
}

const toForm = (rule: IRewardVolumeAllocationRule): IRuleForm => ({
  key: rule.source_account.toLowerCase(),
  persistedSource: rule.source_account,
  sourceAccount: rule.source_account,
  allocations: rule.allocations.map((allocation) => ({
    account: allocation.account,
    percentage: String(allocation.basis_points / 100),
  })),
});

const percentageToBasisPoints = (raw: string): number => {
  const percentage = Number(raw);
  if (!Number.isFinite(percentage)) return NaN;
  const basisPoints = percentage * 100;
  return Math.abs(basisPoints - Math.round(basisPoints)) < 1e-8
    ? Math.round(basisPoints)
    : NaN;
};

const validateRule = (rule: IRuleForm, rules: IRuleForm[]): IRuleValidation => {
  const sourceValid = ethers.utils.isAddress(rule.sourceAccount);
  const duplicateSource =
    sourceValid &&
    rules.filter(
      (candidate) =>
        candidate.sourceAccount.toLowerCase() ===
        rule.sourceAccount.toLowerCase()
    ).length > 1;

  const targetErrors = rule.allocations.map(
    (allocation) => !ethers.utils.isAddress(allocation.account)
  );
  const basisPoints = rule.allocations.map((allocation) =>
    percentageToBasisPoints(allocation.percentage)
  );
  const percentageErrors = basisPoints.map(
    (value) => !Number.isInteger(value) || value <= 0 || value > 10000
  );
  const validTargets = rule.allocations
    .filter((_, index) => !targetErrors[index])
    .map((allocation) => allocation.account.toLowerCase());
  const duplicateTargets = new Set(validTargets).size !== validTargets.length;
  const totalBasisPoints = basisPoints.reduce(
    (sum, value) => sum + (Number.isFinite(value) ? value : 0),
    0
  );
  const valid =
    sourceValid &&
    !duplicateSource &&
    rule.allocations.length > 0 &&
    !targetErrors.some(Boolean) &&
    !percentageErrors.some(Boolean) &&
    !duplicateTargets &&
    totalBasisPoints <= 10000;

  return {
    sourceValid,
    duplicateSource,
    targetErrors,
    percentageErrors,
    duplicateTargets,
    totalBasisPoints,
    valid,
  };
};

export function RewardVolumeAllocations({
  previewRules,
}: IRewardVolumeAllocationsProps = {}) {
  const {
    data,
    isLoading,
    updateRewardVolumeAllocation,
    deleteRewardVolumeAllocation,
  } = useRewardVolumeAllocations();
  const { addToast } = useToast();
  const { records } = useContext(DataContext);
  const accountTags = useMemo(
    () =>
      new Map(
        Object.entries(records).map(([address, name]) => {
          const normalizedAddress = address.trim().toLowerCase();
          const tag = name.trim();
          const prefix = `${normalizedAddress} - `;
          return [
            normalizedAddress,
            tag.toLowerCase().startsWith(prefix)
              ? tag.slice(prefix.length).trim()
              : tag,
          ];
        })
      ),
    [records]
  );
  const renderAccountTag = (address: string) => {
    const tag = address.trim()
      ? accountTags.get(address.trim().toLowerCase())
      : undefined;
    return tag ? <AccountTag>({tag})</AccountTag> : null;
  };
  const [rules, setRules] = useState<IRuleForm[]>([]);
  const [nextDraftId, setNextDraftId] = useState(1);
  const isPreview = previewRules !== undefined;
  const isBusy = isPreview ? false : isLoading;

  useEffect(() => {
    const sourceRules = previewRules ?? data?.rules;
    if (!sourceRules) return;
    setRules(sourceRules.map(toForm));
  }, [data?.rules, previewRules]);

  const validations = useMemo(
    () => rules.map((rule) => validateRule(rule, rules)),
    [rules]
  );

  const updateRule = useCallback(
    (ruleIndex: number, updater: (rule: IRuleForm) => IRuleForm) => {
      setRules((current) =>
        current.map((rule, index) =>
          index === ruleIndex ? updater(rule) : rule
        )
      );
    },
    []
  );

  const addRule = useCallback(() => {
    setRules((current) => [
      {
        key: `draft-${nextDraftId}`,
        persistedSource: null,
        sourceAccount: "",
        allocations: [{ account: "", percentage: "100" }],
      },
      ...current,
    ]);
    setNextDraftId((value) => value + 1);
  }, [nextDraftId]);

  const removeRule = useCallback(
    async (index: number) => {
      const rule = rules[index];
      if (!rule.persistedSource || isPreview) {
        setRules((current) =>
          current.filter((_, ruleIndex) => ruleIndex !== index)
        );
        return;
      }
      if (
        !window.confirm(
          `Remove volume allocation for ${rule.persistedSource}?\n\nFuture weighted volume will remain on the source account.`
        )
      ) {
        return;
      }
      await deleteRewardVolumeAllocation(rule.persistedSource);
    },
    [deleteRewardVolumeAllocation, isPreview, rules]
  );

  const saveRule = useCallback(
    async (index: number) => {
      const rule = rules[index];
      const validation = validations[index];
      if (!validation.valid) return;
      const sourceAccount = ethers.utils.getAddress(rule.sourceAccount);
      const allocations = rule.allocations.map((allocation) => ({
        account: ethers.utils.getAddress(allocation.account),
        basis_points: percentageToBasisPoints(allocation.percentage),
      }));
      if (isPreview) {
        setRules((current) =>
          current.map((candidate, ruleIndex) =>
            ruleIndex === index
              ? {
                  ...candidate,
                  persistedSource: sourceAccount,
                  sourceAccount,
                  allocations: allocations.map((allocation) => ({
                    account: allocation.account,
                    percentage: String(allocation.basis_points / 100),
                  })),
                }
              : candidate
          )
        );
        return;
      }
      const remainder = (10000 - validation.totalBasisPoints) / 100;
      if (
        !window.confirm(
          `Save reward volume allocation?\n\nSource: ${sourceAccount}\nAllocated: ${(
            validation.totalBasisPoints / 100
          ).toFixed(2)}%\nRemainder on source: ${remainder.toFixed(2)}%`
        )
      ) {
        return;
      }
      await updateRewardVolumeAllocation({
        source_account: sourceAccount,
        allocations,
      });
    },
    [isPreview, rules, updateRewardVolumeAllocation, validations]
  );

  const copyMapping = useCallback(async () => {
    const response = { rules: previewRules ?? data?.rules ?? [] };
    try {
      await navigator.clipboard.writeText(JSON.stringify(response, null, 2));
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Volume routing mapping copied",
          subheader: `${response.rules.length} source account${
            response.rules.length === 1 ? "" : "s"
          } copied as JSON`,
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    } catch (error) {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Could not copy volume routing mapping",
          subheader:
            error instanceof Error ? error.message : "Clipboard unavailable",
          status: ToastStatusEnum.ERROR,
        },
        5000
      );
    }
  }, [addToast, data?.rules, previewRules]);

  return (
    <ToolPanel role="tabpanel">
      <PanelToolbar>
        <CardHeader>
          <CardTitle>Volume routing</CardTitle>
          <CardSubtitle>
            Route future reward volume to other accounts. Unallocated volume
            remains with the source.
          </CardSubtitle>
        </CardHeader>
        <PanelToolbarActions>
          <CompactButton
            type="button"
            onClick={copyMapping}
            disabled={
              isBusy || (previewRules ?? data?.rules ?? []).length === 0
            }
          >
            <Copy aria-hidden="true" />
            Copy mapping JSON
          </CompactButton>
          <CompactButton
            type="button"
            primary
            onClick={addRule}
            disabled={isBusy}
          >
            + Add source
          </CompactButton>
        </PanelToolbarActions>
      </PanelToolbar>

      {rules.length === 0 && !isBusy && (
        <EmptyState>No reward volume allocation rules configured.</EmptyState>
      )}

      {rules.length > 0 && (
        <RulesList>
          {rules.map((rule, ruleIndex) => {
            const validation = validations[ruleIndex];
            return (
              <RuleSection key={rule.key}>
                <RuleHeader>
                  <SourceAccount>
                    <SourceLabel>
                      {rule.persistedSource
                        ? "Source account"
                        : "New source account"}
                    </SourceLabel>
                    <AccountWithTag>
                      {rule.persistedSource ? (
                        <SourceAddress title={rule.sourceAccount}>
                          {rule.sourceAccount}
                        </SourceAddress>
                      ) : (
                        <AddressInput
                          aria-label="Trading source account"
                          hasErrors={
                            !validation.sourceValid ||
                            validation.duplicateSource
                          }
                          value={rule.sourceAccount}
                          onChange={(event) =>
                            updateRule(ruleIndex, (current) => ({
                              ...current,
                              sourceAccount: event.target.value,
                            }))
                          }
                          placeholder="0x..."
                          disabled={isBusy}
                        />
                      )}
                      {renderAccountTag(rule.sourceAccount)}
                    </AccountWithTag>
                  </SourceAccount>
                  <IconButton
                    type="button"
                    negative
                    aria-label="Remove source account"
                    title="Remove source account"
                    onClick={() => removeRule(ruleIndex)}
                    disabled={isBusy}
                  >
                    ×
                  </IconButton>
                </RuleHeader>
                {(!validation.sourceValid || validation.duplicateSource) && (
                  <ErrorText>
                    {validation.duplicateSource
                      ? "This source account already has a rule."
                      : "Enter a valid Ethereum source address."}
                  </ErrorText>
                )}

                <AllocationHeader>
                  <span>Recipient account</span>
                  <span>Share</span>
                  <span />
                </AllocationHeader>
                {rule.allocations.map((allocation, allocationIndex) => (
                  <AllocationRow key={`${rule.key}-${allocationIndex}`}>
                    <AccountWithTag>
                      <AddressInput
                        aria-label={`Reward recipient ${allocationIndex + 1}`}
                        hasErrors={validation.targetErrors[allocationIndex]}
                        value={allocation.account}
                        onChange={(event) =>
                          updateRule(ruleIndex, (current) => ({
                            ...current,
                            allocations: current.allocations.map(
                              (item, index) =>
                                index === allocationIndex
                                  ? { ...item, account: event.target.value }
                                  : item
                            ),
                          }))
                        }
                        placeholder="Recipient account (0x...)"
                        disabled={isBusy}
                      />
                      {renderAccountTag(allocation.account)}
                    </AccountWithTag>
                    <PercentageInputWrap>
                      <Input
                        aria-label={`Reward percentage ${allocationIndex + 1}`}
                        hasErrors={validation.percentageErrors[allocationIndex]}
                        value={allocation.percentage}
                        onChange={(event) => {
                          const { value } = event.target;
                          if (
                            value !== "" &&
                            !/^\d{0,3}(\.\d{0,2})?$/.test(value)
                          )
                            return;
                          updateRule(ruleIndex, (current) => ({
                            ...current,
                            allocations: current.allocations.map(
                              (item, index) =>
                                index === allocationIndex
                                  ? { ...item, percentage: value }
                                  : item
                            ),
                          }));
                        }}
                        placeholder="0.00"
                        inputMode="decimal"
                        disabled={isBusy}
                      />
                      <PercentageSuffix>%</PercentageSuffix>
                    </PercentageInputWrap>
                    <IconButton
                      type="button"
                      aria-label={`Remove reward recipient ${
                        allocationIndex + 1
                      }`}
                      title="Remove recipient"
                      onClick={() =>
                        updateRule(ruleIndex, (current) => ({
                          ...current,
                          allocations: current.allocations.filter(
                            (_, index) => index !== allocationIndex
                          ),
                        }))
                      }
                      disabled={isBusy || rule.allocations.length === 1}
                    >
                      ×
                    </IconButton>
                  </AllocationRow>
                ))}

                {validation.duplicateTargets && (
                  <ErrorText>
                    Recipient addresses must be unique within a rule.
                  </ErrorText>
                )}
                {validation.totalBasisPoints > 10000 && (
                  <ErrorText>Total allocation cannot exceed 100%.</ErrorText>
                )}
                <RuleFooter>
                  <AllocationSummary valid={validation.valid}>
                    {(validation.totalBasisPoints / 100).toFixed(2)}% allocated
                    ·{" "}
                    {(
                      Math.max(0, 10000 - validation.totalBasisPoints) / 100
                    ).toFixed(2)}
                    % remains on source
                  </AllocationSummary>
                  <RuleActions>
                    <CompactButton
                      type="button"
                      onClick={() =>
                        updateRule(ruleIndex, (current) => ({
                          ...current,
                          allocations: [
                            ...current.allocations,
                            { account: "", percentage: "" },
                          ],
                        }))
                      }
                      disabled={isBusy || rule.allocations.length >= 100}
                    >
                      + Add recipient
                    </CompactButton>
                    <CompactButton
                      type="button"
                      primary
                      onClick={() => saveRule(ruleIndex)}
                      disabled={isBusy || !validation.valid}
                    >
                      Save changes
                    </CompactButton>
                  </RuleActions>
                </RuleFooter>
              </RuleSection>
            );
          })}
        </RulesList>
      )}
    </ToolPanel>
  );
}
